import type {SbBlokKeyDataTypes} from "@storyblok/js/dist/types/types";

export const getLocalBusinessSchema = () => ({
	"@type": "LocalBusiness",
	name: "VWP Shortlease",
	image: "https://www.vwpshortlease.nl/images/vwp_logo.png",
	address: {
		"@type": "PostalAddress",
		streetAddress: "Energiestraat 8",
		addressLocality: "Nijverdal",
		postalCode: "7442 DA",
		addressCountry: "NL",
	},
	telephone: "+31546714417",
	url: "https://www.vwpshortlease.nl",
	priceRange: "€€",
});

export const getOrgnanizationSchema = () => ({
	"@type": "Organization",
	name: "VWP Shortlease",
	url: "https://www.vwpshortlease.nl",
	logo: {
		"@type": "ImageObject",
		url: "https://www.vwpshortlease.nl/images/vwp_logo.png",
	},
	contactPoint: {
		"@type": "ContactPoint",
		telephone: "+31546714417",
		contactType: "customer service",
	},
});

type BreadCrumbItem = {
	name: any;
	item: any;
};
export const getBreadCrumbSchema = (items: BreadCrumbItem[]) => ({
	"@type": "BreadcrumbList",
	itemListElement: items.map((item: any, index: number) => ({
		"@type": "ListItem",
		position: index + 1,
		name: item.name,
		item: item.item,
	})),
});

export const getArticleSchema = (story: any, baseUrl: any) => ({
	"@type": "Article",
	mainEntityOfPage: {
		"@type": "WebPage",
		"@id": `${baseUrl}/${story.full_slug}`,
	},
	headline: story.content.title || "",
	image: story.content.image ? [story.content.image] : [],
	description: story.content.introText || story.content.description || "",
	datePublished: story.content.datePublished || "",
	dateModified: story.content.dateModified || "",
	author: {
		"@type": "Organization",
		name: "VWP Shortlease",
		url: baseUrl,
		logo: `${baseUrl}/images/vwp_logo.png`,
		contactPoint: {
			"@type": "ContactPoint",
			telephone: "+31546714417",
			contactType: "customer service",
		},
	},
	publisher: {
		"@type": "Organization",
		name: "VWP Shortlease",
		logo: {
			"@type": "ImageObject",
			url: `${baseUrl}/images/vwp_logo.png`,
		},
	},
});

type VideoItem = {
	name: string;
	description: string;
	thumbnailUrl: string;
	uploadDate: string;
	contentUrl: string;
	duration: string;
};
export const getVideoObjectSchema = (item: VideoItem) => ({
	"@type": "VideoObject",
	name: item.name,
	description: item.description,
	thumbnailUrl: item.thumbnailUrl,
	uploadDate: item.uploadDate,
	contentUrl: item.contentUrl,
	embedUrl: item.contentUrl,
	duration: item.duration,
	publisher: {
		"@type": "Organization",
		name: "VWP Shortlease",
		logo: {
			"@type": "ImageObject",
			url: "https://www.vwpshortlease.nl/images/vwp_logo.png",
		},
	},
	interactionStatistic: {
		"@type": "InteractionCounter",
		interactionType: {
			"@type": "WatchAction",
		},
		userInteractionCount: 30,
	},
});

export const getProductSchema = (blok: any, url: string, offer: any) => {
	const info =
		blok.info.filter((item: any) => item.component === "InfoBlock") ?? [];
	return {
		"@type": "Product",
		name: blok.header[0].brand + " " + blok.header[0].model,
		productID: blok.car_id,
		description: info[0]?.text?.content[0]?.content[0]?.text ?? "",
		image: blok.image?.filename ?? "",
		url: url,
		brand: {
			"@type": "Brand",
			name: blok.header[0].brand,
		},
		offers: offer,
	};
};

export const getOfferSchema = (price: string, length: string = "P1M") => ({
	"@type": "Offer",
	price: price,
	priceCurrency: "EUR",
	availability: "https://schema.org/InStock",
	leaseLength: length,
	seller: getOrgnanizationSchema(),
});

type Vehicle = {
	brand: SbBlokKeyDataTypes;
	model: SbBlokKeyDataTypes;
};

export const getVehicleSchema = (vehicle: Vehicle) => ({
	"@type": "Car",
	name: `${vehicle.brand} ${vehicle.model}`,
	brand: {
		"@type": "Brand",
		name: `${vehicle.brand}`,
	},
});

export const getAggregateRatingSchema = (
	ratingValue: number,
	reviewCount: number
) => ({
	"@type": "Organization",
	name: "VWP Shortlease",
	logo: "https://www.vwpshortlease.nl/images/vwp_logo.png",
	url: "https://www.vwpshortlease.nl",
	description:
		"VWP Shortlease is een bedrijf dat kortlopende lease auto's verhuurt.",
	aggregateRating: {
		"@type": "AggregateRating",
		ratingValue: ratingValue,
		reviewCount: reviewCount,
		bestRating: 10,
		itemReviewed: getOrgnanizationSchema(),
	},
});

type Question = {
	question: string;
	answer: string;
};

export const getFAQPageSchema = (questions: Question[]) => ({
	"@type": "FAQPage",
	mainEntity: questions.map((question: Question) => ({
		"@type": "Question",
		name: question.question,
		acceptedAnswer: {
			"@type": "Answer",
			text: question.answer,
		},
	})),
});

type Crumb = {
	position: number;
	name: string;
	item?: string;
};

export const getBreadcrumbListSchema = (breadcrumbs: Crumb[]) => ({
	"@type": "BreadcrumbList",
	itemListElement: breadcrumbs.map((breadcrumb: Crumb, index: number) => ({
		"@type": "ListItem",
		position: index + 1,
		name: breadcrumb.name,
		item: breadcrumb.item,
	})),
});
